import { AddPhotoAlternate, Close } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { UploadPicture } from "../../redux/slices/payment";
import { AddNewGame, UpdateGame } from "../../redux/slices/website";

export default function GamesForm({ open, onClose, selected }) {
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [order, setOrder] = useState("");
  const [error, setError] = useState(null);
  const dispatch = useDispatch();

  useEffect(() => {
    if (open && selected) {
      setOrder(selected.order);
      setImageUrl(selected.url);
    } else {
      setUploading(false);
      setSaving(false);
      setOrder(null);
      setImageUrl("");
      setError(null);
      if (imageUrl && imageUrl.startsWith("blob:")) {
        URL.revokeObjectURL(imageUrl);
      }
    }
  }, [open]);

  const handleUpdate = async () => {
    if (!imageUrl || !order) {
      setError("Please upload an image and enter order number");
      return;
    }

    const orderNum = Number(order);
    if (isNaN(orderNum) || orderNum < 1) {
      setError("Please enter a valid order number (1 or greater)");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const payload = {
        url: imageUrl,
        order: orderNum,
      };

      await dispatch(UpdateGame(selected._id, payload));

      onClose();
    } catch (err) {
      console.error("Update error:", err);
      setError("Failed to update game. Please try again.");
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (selected) {
      return handleUpdate();
    }

    if (!imageUrl || !order) {
      setError("Please upload an image and enter order number");
      return;
    }

    const orderNum = Number(order);
    if (isNaN(orderNum) || orderNum < 1) {
      setError("Please enter a valid order number (1 or greater)");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const requestBody = {
        url: imageUrl,
        order: orderNum,
      };

      await dispatch(AddNewGame(requestBody));

      onClose();
    } catch (err) {
      console.error("Save error:", err);
      setError("Failed to save game. Please try again.");
      setSaving(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setError("Please select a valid image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError("Image size should be less than 5MB");
      return;
    }

    // Show preview immediately
    const previewUrl = URL.createObjectURL(file);
    setImageUrl(previewUrl);
    setError(null);
    setUploading(true);

    try {
      const res = await dispatch(UploadPicture(file));

      if (res) {
        setImageUrl(res);
        setUploading(false);
      } else {
        throw new Error("Upload failed");
      }
    } catch (err) {
      console.error("Upload error:", err);
      setError("Failed to upload image. Please try again.");
      setUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setImageUrl(null);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" fontWeight={600}>
          {selected ? "Edit Game" : "Add New Game"}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          label="Display Order"
          type="number"
          fullWidth
          value={order}
          onChange={(e) => setOrder(e.target.value)}
          sx={{ mt: 2, mb: 3 }}
          placeholder="Enter display order (e.g., 1, 2, 3)"
          inputProps={{ min: 1 }}
          helperText="Lower numbers appear first"
          disabled={uploading || saving}
        />

        {!imageUrl ? (
          <Button
            variant="outlined"
            component="label"
            fullWidth
            disabled={uploading}
            startIcon={<AddPhotoAlternate />}
            sx={{
              py: 2,
              borderRadius: 2,
              borderStyle: "dashed",
              borderWidth: 2,
            }}
          >
            Select Image
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={handleFileChange}
            />
          </Button>
        ) : (
          <Box position="relative">
            <Paper
              elevation={2}
              sx={{
                borderRadius: 2,
                overflow: "hidden",
                position: "relative",
              }}
            >
              <img
                src={imageUrl}
                alt="Preview"
                style={{
                  width: "100%",
                  height: 280,
                  objectFit: "cover",
                }}
              />
              {!uploading && (
                <IconButton
                  onClick={handleRemoveImage}
                  disabled={saving}
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    bgcolor: "error.main",
                    color: "white",
                    "&:hover": {
                      bgcolor: "error.dark",
                    },
                    boxShadow: 2,
                  }}
                  size="small"
                >
                  <Close />
                </IconButton>
              )}
            </Paper>
          </Box>
        )}

        {uploading && (
          <Box mt={2}>
            <LinearProgress />
            <Typography
              variant="caption"
              color="text.secondary"
              mt={1}
              display="block"
            >
              Uploading image...
            </Typography>
          </Box>
        )}

        {saving && (
          <Box mt={2}>
            <LinearProgress color="success" />
            <Typography
              variant="caption"
              color="text.secondary"
              mt={1}
              display="block"
            >
              {selected ? "Updating game..." : "Saving game..."}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={onClose}
          disabled={uploading || saving}
          sx={{ borderRadius: 2 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={uploading || saving || !imageUrl || !order}
          sx={{ borderRadius: 2, minWidth: 100 }}
        >
          {selected ? "Update" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
